import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Search,
  MoreVertical,
  Eye,
  Ban,
  CheckCircle,
  Mail,
  Calendar
} from 'lucide-react';
import { api } from '../../lib/api';
import { useUIStore } from '../../store/ui';
import { formatDate } from '../../lib/utils';

interface Team {
  id: string;
  name: string;
  slug: string;
  contactEmail: string;
  contactPhone?: string;
  status: string;
  memberCount: number;
  bookingCount: number;
  createdAt: string;
  logoUrl?: string;
}

export function AdminTeams() {
  const { addToast } = useUIStore();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  useEffect(() => {
    fetchTeams();
  }, []);

  async function fetchTeams() {
    try {
      const res = await api.get('/api/admin/teams');
      if (res.teams) {
        setTeams(res.teams);
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(teamId: string, newStatus: string) {
    try {
      await api.patch(`/api/admin/teams/${teamId}`, { status: newStatus });
      setTeams(
        teams.map((t) => (t.id === teamId ? { ...t, status: newStatus } : t))
      );
      addToast(`Team ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`, 'success');
    } catch (error) {
      addToast('Failed to update team status', 'error');
    }
    setOpenDropdown(null);
  }

  const filteredTeams = teams.filter(
    (team) =>
      team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.contactEmail.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Teams</h1>
          <p className="text-gray-400 mt-1">
            Manage registered racing teams.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search teams by name or email..."
            className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-10 pr-4 py-2 focus:ring-red-500 focus:border-red-500"
          />
        </div>
      </div>

      {/* Teams Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left p-4 text-sm font-medium text-gray-400">Team</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Contact</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Members</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Bookings</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Status</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Created</th>
                <th className="text-right p-4 text-sm font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredTeams.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center">
                    <Users className="mx-auto text-gray-600 mb-3" size={40} />
                    <p className="text-gray-400">
                      {searchQuery ? 'No teams match your search.' : 'No teams registered yet.'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredTeams.map((team) => (
                  <tr key={team.id} className="hover:bg-gray-750">
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        {team.logoUrl ? (
                          <img
                            src={team.logoUrl}
                            alt=""
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-400">
                              {team.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-white">{team.name}</div>
                          <div className="text-sm text-gray-500">/{team.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-300">
                        <Mail size={14} className="text-gray-500" />
                        <span>{team.contactEmail}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <Users size={14} className="text-gray-500" />
                        <span className="text-gray-300">{team.memberCount}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <Calendar size={14} className="text-gray-500" />
                        <span className="text-gray-300">{team.bookingCount}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          team.status === 'active'
                            ? 'bg-green-900/50 text-green-400'
                            : 'bg-red-900/50 text-red-400'
                        }`}
                      >
                        {team.status}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-400">
                      {formatDate(team.createdAt)}
                    </td>
                    <td className="p-4 text-right">
                      <div className="relative">
                        <button
                          onClick={() =>
                            setOpenDropdown(openDropdown === team.id ? null : team.id)
                          }
                          className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          <MoreVertical size={18} className="text-gray-400" />
                        </button>

                        {openDropdown === team.id && (
                          <div className="absolute right-0 mt-1 w-48 bg-gray-700 rounded-lg shadow-lg border border-gray-600 py-1 z-10">
                            <Link
                              to={`/team/${team.slug}`}
                              className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-600"
                            >
                              <Eye size={14} className="mr-2" />
                              View Team
                            </Link>
                            {team.status === 'active' ? (
                              <button
                                onClick={() => handleStatusChange(team.id, 'suspended')}
                                className="w-full flex items-center px-4 py-2 text-sm text-red-400 hover:bg-gray-600"
                              >
                                <Ban size={14} className="mr-2" />
                                Suspend Team
                              </button>
                            ) : (
                              <button
                                onClick={() => handleStatusChange(team.id, 'active')}
                                className="w-full flex items-center px-4 py-2 text-sm text-green-400 hover:bg-gray-600"
                              >
                                <CheckCircle size={14} className="mr-2" />
                                Activate Team
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
